const META_EVENT_NAMES = {
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  subscribe: "Subscribe",
  start_trial: "StartTrial",
} as const;

type MetaEventName = keyof typeof META_EVENT_NAMES;

declare global {
  interface Window {
    _fbq?: MetaPixelFunction;
    fbq?: MetaPixelFunction;
  }
}

interface MetaPixelFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  getState?: () => { pixels?: { id: string }[] };
  loaded?: boolean;
  queue?: unknown[][];
  version?: string;
}

const metaEventId = (eventName: MetaEventName, dedupKey: string) =>
  `circle:${META_EVENT_NAMES[eventName]}:${dedupKey}`;

export const shouldTrackMetaCompletedAttempt = (attemptStatus: string) =>
  attemptStatus === "completed";

interface PendingEvent {
  dedupKey: string;
  eventName: MetaEventName;
  pixelId: string;
  properties: Record<string, unknown>;
}

const initializedPixelIds = new Set<string>();
let consent: "pending" | "denied" | "granted" = "pending";
const sentEventIds = new Set<string>();
const pendingEvents: PendingEvent[] = [];
const pendingEventIds = new Set<string>();
const MAX_PENDING_EVENTS = 20;

const deliveryKey = ({ eventName, pixelId, dedupKey }: PendingEvent) =>
  `${pixelId}:${metaEventId(eventName, dedupKey)}`;

const hasPixelRuntime = (pixelId: string) =>
  isMetaPixelLoaded(pixelId) || Boolean(window.fbq?.callMethod);

const clearPendingEvents = () => {
  pendingEvents.length = 0;
  pendingEventIds.clear();
};

function flushPendingEvents() {
  if (consent !== "granted") {
    clearPendingEvents();
    return;
  }

  const queuedEvents = pendingEvents.splice(0);
  pendingEventIds.clear();
  queuedEvents.forEach(event => {
    if (hasPixelRuntime(event.pixelId)) {
      send(event);
    } else {
      pendingEvents.push(event);
      pendingEventIds.add(deliveryKey(event));
    }
  });
}

const queuePendingEvent = (event: PendingEvent) => {
  const key = deliveryKey(event);
  if (sentEventIds.has(key) || pendingEventIds.has(key)) return;
  if (pendingEvents.length >= MAX_PENDING_EVENTS) return;

  pendingEvents.push(event);
  pendingEventIds.add(key);
};

const setMetaFbcCookie = (fbc?: string) => {
  if (!fbc) return;

  // Keep the first-touch value visible to the Pixel on checkout without writing it to the tenant's parent domain.
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `_fbc=${encodeURIComponent(fbc)}; Max-Age=7776000; Path=/checkout; SameSite=Lax${secure}`;
};

const loadMetaPixel = (pixelId: string, fbc?: string) => {
  setMetaFbcCookie(fbc);

  if (!window.fbq) {
    // Match Meta's bootstrap: queue before loading, dispatch after callMethod is installed.
    const fbq: MetaPixelFunction = (...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue?.push(args);
    };
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq ??= fbq;
  }

  const fbq = window.fbq;
  if (!fbq) return;

  const wasInitializedByHelper = initializedPixelIds.has(pixelId);
  const isAlreadyInitialized = fbq
    .getState?.()
    .pixels?.some(pixel => pixel.id === pixelId);
  const isInitializationQueued = fbq.queue?.some(
    args => args[0] === "init" && args[1] === pixelId,
  );
  if (
    !wasInitializedByHelper &&
    !isAlreadyInitialized &&
    !isInitializationQueued
  ) {
    if (fbc) fbq("init", pixelId, { fbc });
    else fbq("init", pixelId);
  }
  initializedPixelIds.add(pixelId);

  if (!hasPixelRuntime(pixelId)) {
    const scriptSrc = "https://connect.facebook.net/en_US/fbevents.js";
    const script = document.querySelector<HTMLScriptElement>(
      `script[src="${scriptSrc}"]`,
    );
    if (script) {
      script.addEventListener("load", flushPendingEvents, { once: true });
      script.addEventListener(
        "error",
        () => {
          clearPendingEvents();
          script.remove();
        },
        { once: true },
      );
    } else if (!fbq.callMethod) {
      const script = document.createElement("script");
      script.async = true;
      script.src = scriptSrc;
      script.addEventListener("load", flushPendingEvents, { once: true });
      script.addEventListener(
        "error",
        () => {
          clearPendingEvents();
          script.remove();
        },
        { once: true },
      );
      document.head.appendChild(script);
    }
  }
};

const send = ({ eventName, pixelId, properties, dedupKey }: PendingEvent) => {
  const eventId = metaEventId(eventName, dedupKey);
  const key = `${pixelId}:${eventId}`;
  if (sentEventIds.has(key)) return;

  try {
    loadMetaPixel(pixelId);
    if (!window.fbq) return;
    window.fbq(
      "trackSingle",
      pixelId,
      META_EVENT_NAMES[eventName],
      properties,
      {
        eventID: eventId,
      },
    );
    sentEventIds.add(key);
  } catch {
    // Pixel delivery is best-effort and must never interrupt checkout navigation.
  }
};

export const setMetaPixelConsent = (
  nextConsent: "pending" | "denied" | "granted" | undefined,
  pixelId?: string,
  fbc?: string,
) => {
  try {
    consent = nextConsent ?? "pending";
    if (consent !== "granted" || !pixelId) {
      clearPendingEvents();
      return;
    }

    loadMetaPixel(pixelId, fbc);
    flushPendingEvents();
  } catch {
    // Consent changes must not affect the checkout UI.
  }
};

export const trackMetaEvent = ({
  eventName,
  dedupKey,
  pixelId,
  value,
  currency,
  paymentType,
}: {
  eventName: MetaEventName;
  dedupKey: string;
  pixelId?: string;
  value?: number;
  currency?: string;
  paymentType?: string;
}) => {
  try {
    if (
      !pixelId ||
      value === undefined ||
      !Number.isFinite(value) ||
      value < 0 ||
      !currency ||
      consent !== "granted"
    )
      return;

    const event = {
      eventName,
      pixelId,
      dedupKey,
      properties: { value, currency, payment_type: paymentType },
    };
    if (hasPixelRuntime(pixelId)) {
      send(event);
    } else {
      loadMetaPixel(pixelId);
      if (hasPixelRuntime(pixelId)) send(event);
      else queuePendingEvent(event);
    }
  } catch {
    // Pixel delivery is best-effort and must never interrupt checkout navigation.
  }
};

const isMetaPixelLoaded = (pixelId: string): boolean => {
  const pixels = window.fbq?.getState?.().pixels;
  return pixels?.some(pixel => pixel.id === pixelId) ?? false;
};
